import React, { useState, useRef, useEffect, useCallback } from 'react';
import adminApi from '../api/client';

interface AuthorResult {
  id: string;
  name: string | null;
  name_sinhala: string | null;
}

interface AuthorSearchInputProps {
  label: string;
  /** The free-text value shown in the input */
  value: string;
  /** Called on every keystroke so the parent can store the free-text */
  onChange: (text: string) => void;
  /** Called when the user selects a result from the dropdown */
  onSelect: (id: string, name: string) => void;
  /** Called when the user clears the field (X button) */
  onClear: () => void;
  placeholder?: string;
  /** Optional: currently linked author id — shows a small "linked" badge */
  linkedId?: string | null;
}

const AuthorSearchInput: React.FC<AuthorSearchInputProps> = ({
  label,
  value,
  onChange,
  onSelect,
  onClear,
  placeholder = 'Type to search authors…',
  linkedId,
}) => {
  const [results, setResults] = useState<AuthorResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  // Search only after typing, not when a saved value is loaded/confirmed.
  const [query, setQuery] = useState<string | null>(null);
  const sequence = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const dismissSearch = useCallback(() => {
    sequence.current++;
    setQuery(null); setResults([]); setOpen(false); setSearching(false); setError(false);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        dismissSearch();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dismissSearch]);

  useEffect(() => {
    const token = ++sequence.current;
    setResults([]); setOpen(false); setSearching(false); setError(false);
    if (query === null) return;
    if (query !== value || linkedId || query.trim().length < 2) {
      setQuery(null);
      return;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminApi.get<{ items: AuthorResult[] }>('/admin/master/authors', {
          params: { q: query.trim(), per_page: 8 },
        });
        if (active && token === sequence.current) {
          setResults(res.data.items ?? []); setOpen(true);
        }
      } catch {
        if (active && token === sequence.current) {
          setResults([]); setError(true); setOpen(true);
        }
      } finally {
        if (active && token === sequence.current) setSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [query, value, linkedId]);

  const handleChange = (text: string) => {
    dismissSearch();
    onChange(text);
    if (text.trim().length >= 2) setQuery(text);
  };

  const handleSelect = (author: AuthorResult) => {
    dismissSearch();
    const displayName = author.name || author.name_sinhala || '';
    onChange(displayName);
    onSelect(author.id, displayName);
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {linkedId && (
          <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
            ✓ linked
          </span>
        )}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full border border-gray-200 rounded-xl px-4 py-2 pr-14
            text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        {/* Spinner / clear button */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {searching ? (
            <svg role="status" aria-label="Searching authors" className="animate-spin h-4 w-4 text-gray-400 mr-2" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : null}
          {value ? (
            <button
              type="button"
              aria-label={`Clear ${label}`}
              onClick={() => { dismissSearch(); onChange(''); onClear(); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {results.map((author) => (
            <button
              key={author.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(author); }}
              className="w-full flex flex-col items-start px-4 py-2.5 hover:bg-gray-50
                border-b border-gray-100 last:border-b-0 transition-colors text-left"
            >
              <span className="text-sm font-semibold text-gray-800">
                {author.name || <span className="italic text-gray-400">— no English name —</span>}
              </span>
              {author.name_sinhala && (
                <span className="text-xs text-gray-500">{author.name_sinhala}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {open && error && (
        <div role="alert" className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3">
          <p className="text-sm text-red-600">Authors unavailable. Edit the query to retry.</p>
        </div>
      )}
      {open && !error && !searching && results.length === 0 && value.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3">
          <p className="text-sm text-gray-400 italic">No authors found — free text will be saved as-is.</p>
        </div>
      )}
    </div>
  );
};

export default AuthorSearchInput;
